import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useGetClientVisitorLabReportsQuery,
  type VisitorLabReport
} from "@/api/visitorLabReportApi";
import VisitorLabReportDetails from "../../reports/components/VisitorLabReportDetails";
import { formatUsDate } from "@/utils/date";

const statusOptions = [
  { value: 'NOT_STARTED', label: 'Not Started', color: 'bg-gray-100 text-gray-800' },
  { value: 'WAITING_FOR_RESULT', label: 'Waiting For Result', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'READY_TO_REVIEW', label: 'Ready To Review', color: 'bg-blue-100 text-blue-800' },
  { value: 'CANCELED', label: 'Canceled', color: 'bg-red-100 text-red-800' },
];

const ALL_STATUSES_VALUE = "ALL";

interface ClientVisitorLabReportsProps {
  clientId: number;
}

export default function ClientVisitorLabReports({ clientId }: ClientVisitorLabReportsProps) {
  const [selectedReport, setSelectedReport] = useState<VisitorLabReport | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES_VALUE);

  const { data: reports = [], isLoading } = useGetClientVisitorLabReportsQuery(clientId);

  const filteredReports = statusFilter !== ALL_STATUSES_VALUE
    ? reports.filter(report => report.status === statusFilter)
    : reports;

  const getStatusBadge = (status: string) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? (
      <Badge className={statusOption.color}>{statusOption.label}</Badge>
    ) : <Badge>{status}</Badge>;
  };

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">My Lab Reports</h1>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES_VALUE}>All Statuses</SelectItem>
            {statusOptions.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* REPORTS TABLE */}
      <Card>
        <CardHeader>
          <CardTitle>My Visitor Lab Reports ({filteredReports.length})</CardTitle>
        </CardHeader>

        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b text-left">
                  <th className="p-3">Test Name</th>
                  <th className="p-3">Test Order ID</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Result Document</th>
                  <th className="p-3">Uploaded At</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredReports.map((report) => (
                  <tr key={report.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">{report.testName}</td>
                    <td className="p-3">{report.testOrderId}</td>
                    <td className="p-3">
                      {getStatusBadge(report.status)}
                    </td>
                    <td className="p-3">
                      {report.resultDocumentUrl ? (
                        <Button
                          variant="link"
                          className="p-0 h-auto"
                          onClick={() => window.open(report.resultDocumentUrl, '_blank')}
                        >
                          View Document
                        </Button>
                      ) : (
                        <span className="text-gray-500">No document</span>
                      )}
                    </td>
                    <td className="p-3">
                      {report.uploadedAt ? formatUsDate(report.uploadedAt) : 'N/A'}
                    </td>
                    <td className="p-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedReport(report);
                          setDetailsOpen(true);
                        }}
                      >
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredReports.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No lab reports found.
            </div>
          )}
        </CardContent>
      </Card>

      {/* REPORT DETAILS POPUP */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Lab Report Details</DialogTitle>
          </DialogHeader>

          {selectedReport && (
            <VisitorLabReportDetails
              report={selectedReport}
              onClose={() => setDetailsOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
